
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
    Tab, Tabs, Grid, Box, Button, InputBase, IconButton, Typography,
    Paper, Divider, Snackbar, Alert, Dialog, DialogActions,
    DialogContent, DialogContentText, DialogTitle, Chip, Tooltip
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
//contexts
import { UserContext } from '../contexts';
//react router
import { useNavigate } from 'react-router-dom';
import { DataGridPro, GridCellModes } from '@mui/x-data-grid-pro';
import { dataGridSx } from '../themes/dataGridSx';
import { createRows } from '../methods';
import { getSharedQueries, getUsersQueries, checkDownloads, deleteMySQLQuery, deleteS3File, saveQuery } from '../services';
import { isLicenseExpired, getLicenseStatus } from '../features/userAssets/helpers';

const SavedQuery = ({ handleMenuSelection }) => {
  const theme = useTheme();
    const { userAppSettings, setUserAppSettings, myQueries, setMyQueries, sharedQueries, setSharedQueries } = useContext(UserContext);
    const { savedQueries } = userAppSettings;
    const [loading, setLoading] = useState({
        myQueries: false,
        shared: false
    });
    const { userAssets } = useSelector((store) => store.userAssets);
    const [myQueriesSearchValue, setMyQueriesSearchValue] = useState('');
    const [sharedQueriesSearchValue, setSharedQueriesSearchValue] = useState('');
    const [downloads, setDownloads] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogQueryInfo, setDialogQueryInfo] = useState({});
    const [tabNumber, setTabNumber] = useState(0);

    const [snackbarInfo, setSnackbarInfo] = useState({
        open: false,
        message: "",
        severity: "",
    });

    const [cellModesModel, setCellModesModel] = useState({});

    const myQueriesSearchInputRef = useRef(null);
    const sharedQueriesSearchInputRef = useRef(null);

    const navigate = useNavigate();

    useEffect(() => {
        if (userAppSettings) {
            loadQueries();
        }
    }, [userAppSettings]);

    useEffect(() => {
        if (dialogQueryInfo.queryID !== undefined) {
            handleDialogOpen();
        }
    }, [dialogQueryInfo]);

    const loadQueries = () => {
        setLoading(prevLoading => ({ ...prevLoading, shared: true }));
        getSharedQueries(userAppSettings.id).then((data) => {
            if (data.success) {
                setSharedQueries(data.data.map(x => {
                    return {
                        ...x,
                        MostRecentPatientCount: formatCount(x.MostRecentPatientCount)
                    };
                }));
            } else {
                setSharedQueries([]);
            }
            setLoading(prevLoading => ({ ...prevLoading, shared: false }));
        })
            .catch(err => {
                setLoading(prevLoading => ({ ...prevLoading, shared: false }));
                setSnackbarInfo({
                    open: true,
                    message: err,
                    severity: "error",
                })
            })

        getMyQueries();
        getDownloads();
    }

    const handleDialogOpen = () => {
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };


    const handleOpenQuery = (rowData) => {
        // check license access
        const accessCheck = checkQueryAccess(rowData);

        if (!accessCheck.accessible) {
            setSnackbarInfo({
                open: true,
                message: `Cannot open query: ${accessCheck.reason}. Please contact your administrator for database access.`,
                severity: "error"
            });
            return;
        }

        // show warning for expiring licenses but allow access
        if (accessCheck.status === 'expiring_soon') {
            setSnackbarInfo({
                open: true,
                message: `Warning: Your license for this database expires in ${accessCheck.daysUntilExpiry} days. Query opened successfully.`,
                severity: "warning"
            });
        }

        // check if we have valid data before navigating
        if (rowData.QueryDefinition && typeof rowData.QueryDefinition === 'string') {
            try {
                // Test parse to ensure it's valid JSON before proceeding
                JSON.parse(rowData.QueryDefinition);
                handleMenuSelection('Query Builder');
                navigate(`/queryBuilder/${encodeURIComponent(rowData.QueryID)}`);
            } catch (error) {
                setSnackbarInfo({
                    open: true,
                    message: "Error: Query definition is invalid",
                    severity: "error"
                });
                console.error("Invalid JSON in query definition:", error);
            }
        } else {
            setSnackbarInfo({
                open: true,
                message: "Error: Query definition is missing",
                severity: "error"
            });
        }
    };

    const formatCount = (count) => {
        if (count >= 1000000000) {
            return `${(count / 1000000000).toFixed(2).toLocaleString("en-US")} B`;
        } else if (count > 1000000) {
            return `${(count / 1000000).toFixed(2).toLocaleString("en-US")} M`;
        }

        return count.toLocaleString('en-US');
    };

    const getMyQueries = async () => {
        setLoading(prevLoading => ({ ...prevLoading, myQueries: true }));
        getUsersQueries(userAppSettings.id).then((data) => {
            if (data.success) {
                setMyQueries(data.data.map(x => {
                    let newElement = { ...x };
                    newElement.MostRecentPatientCount = formatCount(x.MostRecentPatientCount);

                    return newElement;
                }));
            } else {
                setMyQueries([]);
            }
            setLoading(prevLoading => ({ ...prevLoading, myQueries: false }));
        })
            .catch(err => {
                setLoading(prevLoading => ({ ...prevLoading, myQueries: false }));
                setSnackbarInfo({
                    open: true,
                    message: err,
                    severity: "error",
                })
            });
    };

    const getDownloads = () => {
        checkDownloads().then((data) => {
            const parsedData = JSON.parse(data.data);

            if (data.success && parsedData !== undefined && parsedData !== null) {
                setDownloads(parsedData.contents);
            } else {
                setDownloads([]);
            }
        });
    };

    const refreshMyQueries = () => {
        getMyQueries();
        getDownloads();
    }

    const refreshSharedQueries = () => {
        getSharedQueries();
        getDownloads();
    }

    const filteredMyQueries = myQueries?.filter(query => {
        return query.QueryName.toLowerCase().includes(myQueriesSearchValue.toLowerCase())
    });

    const filteredSharedQueries = sharedQueries?.filter(query => {
        return query.QueryName.toLowerCase().includes(sharedQueriesSearchValue.toLowerCase())
    });

    const incrementQueryName = (queryName) => {
        let foundUnusedName = false;
        let foundUnusedNumber = 1;

        while (!foundUnusedName) {
            if (savedQueries?.filter((x) => x.name === `${queryName} Copy ${foundUnusedNumber}`).length >= 1) {
                foundUnusedNumber++;
            }

            foundUnusedName = true;
        }

        return ` Copy ${foundUnusedNumber}`;
    }

    const handleTabChange = (event, newTabNumber) => {
        setTabNumber(newTabNumber);
    }

    // Zips are named queryName_runId.zip, older ones just queryName.zip.
    // Newest first.
    const findDownloadsForQuery = (queryName) => {
        if (!queryName) {
            return [];
        }

        return (downloads ?? [])
            .filter(x =>
                x.FileName === `${queryName}.zip` ||
                (x.FileName?.startsWith(`${queryName}_`) && x.FileName?.endsWith('.zip'))
            )
            .sort((a, b) => (a.FileName < b.FileName ? 1 : -1));
    };

    const handleDeleteQuery = async () => {
        try {
            // Delete GraphQL
            /*
            let obj = {
                id: userAppSettings.id,
                savedQueries: savedQueries.filter(q => q.name !== queryName)
            }
            const userSettings = await API.graphql({ query: mutations.updateUserAppSettings, variables: { input: obj } });

            if (userSettings && userSettings.data) {
                setUserAppSettings(userSettings?.data?.updateUserAppSettings)
            }
            */

            // Delete MySQL Query
            const deleteMySQLQueryResult = await deleteMySQLQuery(dialogQueryInfo.queryID);

            // Delete S3 Files
            if (deleteMySQLQueryResult.data.affectedRows > 0) {
                // Needs every run's files here, not just the newest
                const downloadedFiles = findDownloadsForQuery(dialogQueryInfo.queryName);

                if (downloadedFiles?.length >= 1) {
                    const myInit = {
                        body: {
                            keys: downloadedFiles.map((x) => x.Key)
                        }
                    };

                    const deleteS3FileResult = await deleteS3File(myInit);

                    console.log(deleteS3FileResult);
                }
            }

            refreshMyQueries();
        }
        catch (error) {
            console.log(error);
        }
    };

    const handleDownload = async (queryName) => {
        const file = findDownloadsForQuery(queryName)[0];

        if (!file) {
            return setSnackbarInfo({
                open: true,
                message: "No generated file was found for this query.",
                severity: "error",
            });
        }

        window.open(file.DownloadURL);
    };

    const queryNameIsInDownloads = (queryName) => {
        return findDownloadsForQuery(queryName).length > 0;
    };

    const copyQuery = (query, additionalNamePart) => {
        const myInit = {
            body: {
                queryID: userAppSettings.id + new Date().toISOString(),
                userID: userAppSettings.id,
                queryName: `${query.QueryName}${additionalNamePart}`,
                queryDescription: query.QueryDescription ?? '',
                queryDefinition: query.QueryDefinition,
                tags: query.Tags ?? [],
                isShareable: false,
                db: query.DatabaseName,
                executedSQL: query.ExecutedSQL,
                mostRecentPatientCount: Number(query.MostRecentPatientCount.replace(/\D/g, '')),
                moveDownload: true,
                originalQueryName: query.QueryName,
                originalQueryID: query.QueryID
            }
        };

        saveQuery(myInit).then(async (response) => {
            if (response.success) {
                const newQueryName = `${query.QueryName}${additionalNamePart}`;
                const newQueryId = myInit.body.queryID;

                const newQuery = {
                    ...query,
                    QueryName: newQueryName,
                    QueryID: newQueryId,
                    MostRecentPatientCount: formatCount(query.MostRecentPatientCount)
                };

                setMyQueries(prevQueries => [...prevQueries, newQuery]);

                setSnackbarInfo({
                    open: true,
                    message: 'Query Copied Successfully!',
                    severity: "success",
                });

                handleMenuSelection('Query Builder');
                navigate(`/queryBuilder/${encodeURIComponent(newQueryId)}`);
            } else {
                setSnackbarInfo({
                    open: true,
                    message: 'Failed to copy query.',
                    severity: "error",
                });
            }
        }).catch(err => {
            setSnackbarInfo({
                open: true,
                message: err.message,
                severity: "error",
            });
        });
    };

    const renderMyQueryActions = (params) => {
        const rowData = params.row;
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 1
            }}>
                <Tooltip title="Open">
                    <IconButton
                        size="small"
                        onClick={() => handleOpenQuery(rowData)}
                        sx={{
                            color: 'info.main',
                            p: 0.5,
                            '&:hover': { backgroundColor: alpha('#2196f3', 0.12) }
                        }}
                    >
                        <OpenInNewIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Copy & Open">
                    <IconButton
                        size="small"
                        onClick={() => copyQuery(rowData, incrementQueryName(rowData.QueryName))}
                        sx={{
                            color: 'primary.main',
                            p: 0.5,
                            '&:hover': { backgroundColor: alpha('#04aeea', 0.12) }
                        }}
                    >
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Delete">
                    <IconButton
                        size="small"
                        onClick={() => {
                            setDialogQueryInfo({
                                queryID: rowData.QueryID,
                                queryName: rowData.QueryName
                            });
                        }}
                        sx={{
                            color: 'error.main',
                            p: 0.5,
                            '&:hover': { backgroundColor: alpha('#f44336', 0.12) }
                        }}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Download">
                    <span>
                        <IconButton
                            size="small"
                            onClick={() => handleDownload(rowData.QueryName)}
                            disabled={!queryNameIsInDownloads(rowData.QueryName)}
                            sx={{
                                color: queryNameIsInDownloads(rowData.QueryName) ? 'info.main' : 'text.disabled',
                                p: 0.5,
                                '&:hover': { backgroundColor: alpha('#2196f3', 0.12) }
                            }}
                        >
                            <CloudDownloadIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        );
    };

    const renderSharedQueryActions = (params) => {
        const rowData = params.row;

        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 1
            }}>
                <Tooltip title="Copy & Open">
                    <IconButton
                        size="small"
                        onClick={() => copyQuery(rowData, incrementQueryName(rowData.QueryName))}
                        sx={{
                            color: 'primary.main',
                            p: 0.5,
                            '&:hover': { backgroundColor: alpha('#04aeea', 0.12) }
                        }}
                    >
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        );
    };

    const renderDatabaseBadge = (params) => {
        // Get the full database name
        const fullDatabaseName = params.value;

        // Extract the type part from database name (everything after the dash if there is one)
        const databaseType = fullDatabaseName.includes('-')
            ? fullDatabaseName.split('-')[1]
            : fullDatabaseName;

        // Now map the extracted type to colors
        const dbColors = {
            'CARDIO': '#00b0ff',
            'PSYCH': '#7e57c2'
        };

        // Get color based on the extracted type, with fallback
        const color = dbColors[databaseType] || '#78909c';

        return (
            <Chip
                label={fullDatabaseName}
                size="small"
                sx={{
                    backgroundColor: alpha(color, 0.12),
                    color: color,
                    fontWeight: 500,
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    height: '24px',
                    '& .MuiChip-label': { px: 1.5 }
                }}
            />
        );
    };

    const renderTags = (params) => {
        if (!params.value || typeof params.value !== 'string') return null;

        const tags = params.value.split(',').map(tag => tag.trim()).filter(tag => tag);

        if (tags.length === 0) return null;

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {tags.map((tag, index) => (
                    <Chip
                        key={index}
                        label={tag}
                        size="small"
                        sx={{
                            backgroundColor: 'surface.subtle',
                            border: '1px solid',
                            borderColor: 'divider',
                            fontSize: '0.75rem'
                        }}
                    />
                ))}
            </Box>
        );
    };

    const renderPatientCount = (params) => {
        const count = params.value;
        const isLarge = count > 100000;

        return (
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 500,
                    color: isLarge ? 'warning.main' : 'primary.main',
                    fontSize: '0.875rem'
                }}
            >
                {typeof count === 'number'
                    ? count.toLocaleString('en-US')
                    : count}
            </Typography>
        );
    };

    const renderUpdatedDate = (params) => {
        if (!params.value) return null;

        const date = new Date(params.value);
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        const isYesterday = date.toDateString() === yesterday.toDateString();

        // Format time consistently
        const timeString = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });

        let formattedDate;
        if (isToday) {
            formattedDate = `Today at ${timeString}`;
        } else if (isYesterday) {
            formattedDate = `Yesterday at ${timeString}`;
        } else {
            // For older dates, show month/day
            formattedDate = `${date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            })} at ${timeString}`;
        }

        return (
            <Typography
                variant="body2"
                sx={{
                    fontSize: '0.875rem',
                    color: isToday ? 'primary.main' : 'text.secondary'
                }}
            >
                {formattedDate}
            </Typography>
        );
    };

    const columns = [
        { field: 'queryID', headerName: 'ID', hide: true },
        {
            field: 'QueryName',
            headerName: 'Title',
            width: 200,
            display: 'flex',
            renderCell: (params) => (
                <Typography variant="body2" fontWeight="medium" noWrap sx={{ width: '100%' }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'QueryDescription',
            headerName: 'Description',
            width: 200,
            flex: 1,
            display: 'flex',
            // Flex cells lose the grid's built-in ellipsis, so truncate here.
            renderCell: (params) => (
                <Typography variant="body2" noWrap sx={{ width: '100%' }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'DatabaseName',
            headerName: 'Database',
            width: 150,
            // `display: 'flex'` is required for non-text renderCell content —
            // the grid only vertically centers plain text via line-height.
            display: 'flex',
            renderCell: renderDatabaseBadge
        },
        {
            field: 'UpdatedDate',
            headerName: 'Updated',
            width: 150,
            display: 'flex',
            renderCell: renderUpdatedDate
        },
        {
            field: 'Tags',
            headerName: 'Tags',
            width: 180,
            display: 'flex',
            renderCell: renderTags
        },
        {
            field: 'MostRecentPatientCount',
            headerName: 'Patients',
            width: 120,
            display: 'flex',
            renderCell: renderPatientCount,
            headerAlign: 'center',
            align: 'center'
        },
    ];

    const userColumns = [
        ...columns,
        {
            field: 'action',
            headerName: 'Actions',
            renderCell: renderMyQueryActions,
            width: 150,
            display: 'flex',
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            filterable: false
        },
    ];

    const sharedColumns = [
        ...columns,
        {
            field: 'action',
            headerName: 'Actions',
            renderCell: renderSharedQueryActions,
            width: 150,
            display: 'flex',
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            filterable: false
        },
    ];

    const TabPanel = (props) => {
        const { children, index, ...other } = props;

        return (
            <div
                role="tabpanel"
                hidden={tabNumber !== index}
                id={`tabpanel-${index}`}
                aria-labelledby={`tabpanel-${index}`}
                {...other}
            >
                {tabNumber === index && (
                    <Box sx={{ p: 1 }}>
                        {children}
                    </Box>
                )}
            </div>
        );
    };

    const DeleteDialog = () => {
        return (
            <div>
                <Dialog
                    open={dialogOpen}
                    onClose={handleDialogClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">
                        {"Delete Query?"}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Are you sure you want to delete this query?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => { handleDeleteQuery(); handleDialogClose(); }}>
                            Yes
                        </Button>
                        <Button onClick={handleDialogClose} autoFocus>
                            No
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    };

    const handleClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbarInfo({ ...snackbarInfo, open: false });
    };

    const checkQueryAccess = (query) => {
        if (!query.DatabaseName || !userAssets.length) {
            return {
                accessible: false,
                reason: 'Database information not available',
                status: 'unknown'
            };
        }

        const asset = userAssets.find(asset => asset.AssetName === query.DatabaseName);

        if (!asset) {
            return {
                accessible: false,
                reason: 'You do not have access to this database',
                status: 'no_access'
            };
        }

        const licenseStatus = getLicenseStatus(asset);

        return {
            accessible: licenseStatus.status !== 'expired',
            reason: licenseStatus.status === 'expired' ? 'License has expired' : null,
            status: licenseStatus.status,
            daysUntilExpiry: licenseStatus.daysUntilExpiry
        };
    };

    return (
        <Grid container sx={{ height: '90vh', backgroundColor: 'surface.page', padding: 2 }}>
            <Paper sx={{ width: '100%', p: 3, boxShadow: 2, borderRadius: 1 }}>
                <Box sx={{ width: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Tabs value={tabNumber} onChange={handleTabChange} variant="fullWidth" textColor="primary" indicatorColor="primary">
                            <Tab label="My Queries" />
                            <Tab label="Queries shared by others" />
                        </Tabs>
                    </Box>
                    <TabPanel value={tabNumber} index={0}>
                        <Paper sx={{ alignItems: 'center', backgroundColor: 'background.paper', borderRadius: 2, width: '100%', boxShadow: 0 }}>
                            <Grid size={12} padding={2}>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 3,
                                    width: '100%'
                                }}>
                                    <Box component="div" sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: 'surface.sunken',
                                        border: "1px solid",
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        width: '100%',
                                        flex: 1,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                        transition: 'all 0.2s ease',
                                        '&:focus-within': {
                                            boxShadow: '0 2px 8px rgba(4,174,234,0.15)',
                                            borderColor: alpha('#04aeea', 0.4)
                                        }
                                    }}>
                                        <SearchIcon sx={{ color: 'text.secondary', ml: 1.5 }} />
                                        <InputBase
                                            fullWidth
                                            value={myQueriesSearchValue}
                                            placeholder="Search queries..."
                                            inputProps={{
                                                key: "myQueriesSearchInput",
                                                autoFocus: myQueriesSearchValue.length > 0
                                            }}
                                            sx={{
                                                ml: 1,
                                                flex: 1,
                                                py: 1,
                                                fontSize: '0.95rem'
                                            }}
                                            onChange={(event) => {
                                                setMyQueriesSearchValue(event.target.value);
                                            }}
                                            endAdornment={
                                                myQueriesSearchValue.length > 0 &&
                                                <IconButton
                                                    sx={{ p: '10px' }}
                                                    aria-label="cancel"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setMyQueriesSearchValue('');
                                                    }}
                                                >
                                                    <CancelOutlinedIcon fontSize='small' />
                                                </IconButton>
                                            }
                                        />
                                    </Box>

                                    <Tooltip title="Refresh">
                                        <IconButton
                                            color="primary"
                                            onClick={refreshMyQueries}
                                            sx={{
                                                border: '1px solid',
                                                borderColor: alpha('#04aeea', 0.3),
                                                borderRadius: 1.5,
                                                p: 1,
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                                ml: 1.5
                                            }}
                                        >
                                            <RefreshIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Grid>
                            <Grid size={12} padding={1}>
                                <DeleteDialog />
                                <DataGridPro
                                    sx={{ ...dataGridSx(theme), height: '600px' }}
                                    loading={loading.myQueries}
                                    columns={userColumns}
                                    rows={createRows(filteredMyQueries)}
                                    disableRowSelectionOnClick
                                    initialState={{
                                        sorting: {
                                            sortModel: [{ field: 'UpdatedDate', sort: 'desc' }]
                                        }
                                    }}
                                    rowHeight={56}
                                />
                            </Grid>
                        </Paper>
                    </TabPanel>
                    <TabPanel value={tabNumber} index={1}>
                        <Paper sx={{ alignItems: 'center', backgroundColor: 'background.paper', borderRadius: 1, width: '100%', boxShadow: 0 }}>
                            <Grid size={12} padding={2}>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 3,
                                    width: '100%'
                                }}>
                                    <Box component="div" sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: 'surface.sunken',
                                        border: "1px solid",
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        width: '100%',
                                        flex: 1,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                        transition: 'all 0.2s ease',
                                        '&:focus-within': {
                                            boxShadow: '0 2px 8px rgba(4,174,234,0.15)',
                                            borderColor: alpha('#04aeea', 0.4)
                                        }
                                    }}>
                                        <SearchIcon sx={{ color: 'text.secondary', ml: 1.5 }} />
                                        <InputBase
                                            fullWidth
                                            value={sharedQueriesSearchValue}
                                            placeholder={"Search for saved queries"}
                                            inputProps={{
                                                key: "sharedQueriesSearchInput",
                                                autoFocus: sharedQueriesSearchValue.length > 0
                                            }}
                                            sx={{
                                                ml: 1,
                                                flex: 1,
                                                py: 1,
                                                fontSize: '0.95rem'
                                            }}
                                            onChange={(event) => {
                                                setSharedQueriesSearchValue(event.target.value);
                                            }}
                                            endAdornment={
                                                sharedQueriesSearchValue.length > 0 &&
                                                <IconButton
                                                    sx={{ p: '10px' }}
                                                    aria-label="cancel"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSharedQueriesSearchValue('');
                                                    }}
                                                >
                                                    <CancelOutlinedIcon fontSize='small' />
                                                </IconButton>
                                            }
                                        />
                                    </Box>
                                    <Tooltip title="Refresh">
                                        <IconButton
                                            color="primary"
                                            onClick={refreshSharedQueries}
                                            sx={{
                                                border: '1px solid',
                                                borderColor: alpha('#04aeea', 0.3),
                                                borderRadius: 1.5,
                                                p: 1,
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                                ml: 1.5
                                            }}
                                        >
                                            <RefreshIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Grid>
                            <Grid size={12} padding={1}>
                                <DataGridPro
                                    sx={{ ...dataGridSx(theme), height: '600px' }}
                                    loading={loading.shared}
                                    edit
                                    columns={sharedColumns}
                                    rows={createRows(filteredSharedQueries)}
                                    disableRowSelectionOnClick
                                    rowCount={filteredSharedQueries.length}
                                    initialState={{
                                        sorting: {
                                            sortModel: [{ field: 'UpdatedDate', sort: 'desc' }]
                                        }
                                    }}
                                />
                            </Grid>
                        </Paper>
                    </TabPanel>
                </Box>
            </Paper>
            <Box>
                <Snackbar open={snackbarInfo.open} autoHideDuration={snackbarInfo.hideDuration || 6000} onClose={() => handleClose()} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                    <Alert onClose={() => handleClose()} severity={snackbarInfo.severity || 'info'} sx={{ width: '100%' }}>
                        {snackbarInfo.message}
                    </Alert>
                </Snackbar>
            </Box>
        </Grid>
    );
}

export default SavedQuery;
